import { Inject, Injectable, PLATFORM_ID } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { isPlatformBrowser, isPlatformServer } from '@angular/common';
import { makeStateKey, TransferState } from '@angular/platform-browser';
import { BehaviorSubject, Observable, of, ReplaySubject } from 'rxjs';
import { distinctUntilChanged, filter, switchMap } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { OnlineProduct } from '../interfaces/product';
import { StoreService } from './store.service';
import { CountryGateService } from './country-gate.service';

/// What a card shows in the visitor's country, in US dollars: the figures its product page opens on there. Own is the price
/// the answer is for.
export interface CardPrice
{
    Id: number;
    /// The PUID the ask named, back as it came.
    Puid?: string;
    Variant?: string;
    Own: number;
    Price: number;
    RegularPrice: number;
    Discount: number;
}

/// Id is the card's LISTING row id (what OnlineProduct.ID is) - not the product's; the server finds the product by it.
/// Puid, where the product carries one, is what the server goes by instead: a listing row and its product share it, so
/// it is right for the wishlist and the compare list too, which hold listing rows (put there from a card) AND products
/// (put there from the product page, whose ID is the product's own) - an ID alone cannot say which it is.
interface CardAsk { Id: number; Puid: string | null; Variant: string | null; Price: number; RegularPrice: number; }

/// What a server render asked, carried to the browser: the country it was drawn for, and each card's answer.
interface Carried { Country: string; Prices: { [card: string]: CardPrice | null }; }

/// A card's price for the visitor's country (CardPriceController), so a card shows what its product page opens on: the
/// small-fee surcharge folded into the price, the part of a large delivery fee the price carries, or the price of the
/// warehouse that delivers there. The listing's own figures stand until the answer is in, and wherever the country
/// changes nothing.
///
/// Every card that asks in the same moment goes in one request, and an answer is kept for the visit, so a screen of cards
/// costs one small call and coming back to it costs none. It asks once the visitor's country is known, as the product page
/// asks for its offer (ProductComponent.loadMyOffer): in the browser, and on a server render drawn for a returning visitor
/// (StoreService.drawnForVisitor), which carries the answers in the page, so the first paint already shows them and the
/// browser does not ask again. A crawler's render and a first visit ask nothing on the server.
@Injectable({ providedIn: 'root' })
export class CardPriceService
{
    private static readonly carriedKey = makeStateKey<Carried>('izz-card-prices');

    /// Cards per request - what the server answers at most (CardPrices.MaxCards).
    private static readonly batch = 60;

    private readonly url = environment.apiUrl + '/api/CardPrice';
    private readonly country$ = new BehaviorSubject<string>('');

    /// Answers by "country|product (its PUID, else its id)|colour|own price"; null is "the listing's own figures".
    private readonly known = new Map<string, CardPrice | null>();
    private readonly waiting = new Map<string, ReplaySubject<CardPrice | null>>();
    private queue: { country: string, key: string, ask: CardAsk }[] = [];
    private flushTimer: any = null;
    private carried: Carried = null;

    constructor(private http: HttpClient, private store: StoreService, private gate: CountryGateService,
                private state: TransferState, @Inject(PLATFORM_ID) private platformId: any)
    {
        if (isPlatformBrowser(this.platformId) && this.state.hasKey(CardPriceService.carriedKey))
        {
            const carried = this.state.get<Carried>(CardPriceService.carriedKey, null);
            this.state.remove(CardPriceService.carriedKey);
            if (carried && carried.Country && carried.Prices)
            {
                Object.keys(carried.Prices).forEach(card => this.known.set(carried.Country + '|' + card, carried.Prices[card]));
            }
        }

        // Not the guess made before Locale/me answers (Pending): the cards stay as drawn until the country is the visitor's.
        this.store.onCountry.subscribe(info =>
        {
            if (!info || info.Pending) { return; }
            if (isPlatformServer(this.platformId) && !this.store.drawnForVisitor) { return; }
            const code = (this.gate.country() || info.CountryCode || '').toUpperCase();
            if (/^[A-Z]{2}$/.test(code)) { this.country$.next(code); }
        });
    }

    /// The card's figures in the visitor's country, or null where they are the listing's own. Answers again when the
    /// country changes (the owner seeing the shop as another country).
    priceFor(product: OnlineProduct): Observable<CardPrice | null>
    {
        // A card reading "Upcoming" has no price to put anything on.
        if (!product || !(product.ID > 0) || !(product.Price > 0)) { return of(null); }

        const variant = product.ShowcaseVariant || null;
        const puid = product.PUID || null;
        const card = CardPriceService.card(product.ID, puid, variant, product.Price);
        const ask = { Id: product.ID, Puid: puid, Variant: variant, Price: product.Price, RegularPrice: product.RegularPrice > 0 ? product.RegularPrice : 0 };
        return this.country$.pipe(
            filter(country => !!country),
            distinctUntilChanged(),
            switchMap(country => this.ask(country, card, ask)));
    }

    /// One card's key: the product (by its PUID where it has one, as it is asked), the colour it shows and the price it
    /// shows - the answer is worked out from that price.
    private static card(id: number, puid: string | null, variant: string | null, own: number): string
    {
        return (puid || id) + '|' + (variant || '') + '|' + own;
    }

    private ask(country: string, card: string, ask: CardAsk): Observable<CardPrice | null>
    {
        const key = country + '|' + card;
        if (this.known.has(key)) { return of(this.known.get(key)); }

        let answer = this.waiting.get(key);
        if (!answer)
        {
            answer = new ReplaySubject<CardPrice | null>(1);
            this.waiting.set(key, answer);
            this.queue.push({ country, key, ask });

            // Every card drawn in this pass asks before the timer fires: one request for the lot.
            if (!this.flushTimer) { this.flushTimer = setTimeout(() => this.flush(), 0); }
        }
        return answer.asObservable();
    }

    private flush(): void
    {
        this.flushTimer = null;
        const queue = this.queue;
        this.queue = [];

        const byCountry = new Map<string, { country: string, key: string, ask: CardAsk }[]>();
        queue.forEach(q => byCountry.set(q.country, (byCountry.get(q.country) || []).concat([q])));

        byCountry.forEach((items, country) =>
        {
            for (let at = 0; at < items.length; at += CardPriceService.batch)
            {
                const part = items.slice(at, at + CardPriceService.batch);
                this.http.post<CardPrice[]>(this.url, { Country: country, Items: part.map(p => p.ask) }).subscribe(prices =>
                {
                    const found = new Map<string, CardPrice>();
                    (prices || []).forEach(p => found.set(country + '|' + CardPriceService.card(p.Id, p.Puid || null, p.Variant || null, Number(p.Own)), p));
                    part.forEach(p => this.settle(country, p.key, found.get(p.key) || null, true));
                }, () =>
                {
                    // The listing's own figures stand, and a later screen may ask again.
                    part.forEach(p => this.settle(country, p.key, null, false));
                });
            }
        });
    }

    private settle(country: string, key: string, price: CardPrice | null, keep: boolean): void
    {
        if (keep)
        {
            this.known.set(key, price);
            if (isPlatformServer(this.platformId)) { this.carry(country, key, price); }
        }

        const answer = this.waiting.get(key);
        this.waiting.delete(key);
        if (answer)
        {
            answer.next(price);
            answer.complete();
        }
    }

    /// On the server: what this render asked goes into the page, for the country it was drawn for.
    private carry(country: string, key: string, price: CardPrice | null): void
    {
        if (!this.carried || this.carried.Country !== country) { this.carried = { Country: country, Prices: {} }; }
        this.carried.Prices[key.substring(country.length + 1)] = price;
        this.state.set(CardPriceService.carriedKey, this.carried);
    }
}
